import React from "react"
import styled from "styled-components"
import Container from "./Container"
import H6 from "../typography/H6"
import P from "../typography/P"
import { Link } from "gatsby"
import { fontRegular } from "../../utils/fontFamily"
import MindMeLogoWhite from "../../images/branding/mindme-logo-white.svg"
import AppStore from "../../images/branding/app-store.svg"
import FacebookIcon from "../../images/icons/facebook-footer-icon.svg"
import TwitterIcon from "../../images/icons/twitter-footer-icon.svg"
import LinkedInIcon from "../../images/icons/linkedin-footer-icon.svg"
import config from "../../config/config"
import { useTheme } from "../../context/ThemeContext"

const date = new Date()
const currentYear = date.getFullYear()

const copyWriteText = `© ${currentYear} MindMe, Inc. All rights reserved.`

const footerMenu = (pricingLink) => {
  return [
    {
      menuName: "Platform",
      menuLinks: [
        {
          name: "Sales Funnel",
          url: "/platform/salesfunnel",
        },
        {
          name: "Text & Email Campaigns",
          url: "/platform/campaigns",
        },

        {
          name: "Contact Management",
          url: "/platform/contact-management",
        },
        {
          name: "Mobile Landing Pages",
          url: "/platform/mobile-landing-pages",
        },
        {
          name: "Advanced Automation",
          url: "/platform/automation",
        },
        {
          name: "Payments & Billing",
          url: "/platform/payments-billing",
        },
        {
          name: "Digital Offers",
          url: "/platform/offers",
        },
      ],
    },
    {
      menuName: "Features",
      menuLinks: [
        {
          name: "Intelligent Delivery",
          url: "/features/intelligent-delivery",
        },
        {
          name: "Custom Domains",
          url: "/features/custom-domains",
        },

        {
          name: "Smart Lists & Filters",
          url: "/features/smartlists-and-filters",
        },
        {
          name: "Library + 2m Free Images",
          url: "/features/library",
        },
        {
          name: "Actions",
          url: "/features/actions",
        },
      ],
    },
    {
      menuName: "Resources",
      menuLinks: [
        {
          name: "Take a Tour",
          url: "/resources/tour",
        },
        {
          name: "Mobile App",
          url: "/platform/app",
        },
        {
          name: "Plans & Pricing",
          url: pricingLink,
        },
        {
          name: "Knowledgebase",
          url: "https://knowledgebase.mindmemobile.com",
          newWindow: true,
        },
        {
          name: "Blog",
          url: "https://blog.mindmemobile.com",
          newWindow: true,
        },
      ],
    },
  ]
}

const bottomBarMenu = [
  {
    name: "Mobile Terms",
    url: "/mobile-terms",
  },
  {
    name: "Terms & Conditions",
    url: "/terms-and-conditions",
  },
  {
    name: "Privacy Policy",
    url: "/privacy-policy",
  },
  {
    name: "Disclaimer",
    url: "/disclaimer",
  },
]

const contactData = [
  {
    label: "+1 (855) 551-0500",
    action: "tel:8555510500",
  },
  {
    label: "support@mindmemobile.com",
    action: "mailto:support@mindmemobile.com",
  },
]

const socialFooterIcons = [
  {
    icon: FacebookIcon,
    alt: "MindMe Facebook Profile",
    link: "https://www.facebook.com/mindmemobile",
  },
  {
    icon: TwitterIcon,
    alt: "MindMe Twitter Profile",
    link: "https://twitter.com/mindmemobile",
  },
  {
    icon: LinkedInIcon,
    alt: "MindMe LinkedIn Profile",
    link: "https://www.linkedin.com/company/mindmemobile",
  },
]

const Footer = () => {
  const themeToggle = useTheme()
  const pricingLink = `/pricing${themeToggle.getPromoQueryParam()}`
  const footerMenus = footerMenu(pricingLink)
  return (
    <FooterWrapper>
      <FooterLinksContainer>
        {footerMenus.map((menu) => {
          const { menuName, menuLinks } = menu
          return (
            <FooterMenuContainer key={menuName}>
              <FooterMenuHeader>{menuName}</FooterMenuHeader>
              <nav>
                <FooterMenuUnorderedList>
                  {menuLinks.map((link) => {
                    const { name, url, newWindow } = link
                    return (
                      <FooterMenuListItem key={name + url}>
                        <FooterMenuLink to={url} target={newWindow && "_blank"}>
                          {name}
                        </FooterMenuLink>
                      </FooterMenuListItem>
                    )
                  })}
                </FooterMenuUnorderedList>
              </nav>
            </FooterMenuContainer>
          )
        })}
        <FooterMenuContainer className="footer-menu-container">
          <FooterMenuHeader>Contact Us</FooterMenuHeader>
          <nav>
            <FooterMenuUnorderedList>
              {/* <FooterMenuListItem>
                <FooterMenuLink to="https://meetings.hubspot.com/bert/mindme-demo">
                  Schedule Demo
                </FooterMenuLink>
              </FooterMenuListItem> */}
              <FooterMenuListItem>
                <FooterMenuLink to="/support">Get Support</FooterMenuLink>
              </FooterMenuListItem>
              {/* <FooterMenuListItem>
              <FooterMenuLink
                to={`${config.WEB_ENDPOINT}affiliate/login`}
                target="_blank"
              >
                Become an Affiliate
              </FooterMenuLink>
            </FooterMenuListItem> */}
            </FooterMenuUnorderedList>
          </nav>
          <br />
          <FooterMenuHeader>Access</FooterMenuHeader>
          <nav>
            <FooterMenuUnorderedList>
              <FooterMenuListItem>
                <FooterMenuLink to={`${config.WEB_ENDPOINT}login`}>
                  Account Login
                </FooterMenuLink>
              </FooterMenuListItem>
              <FooterMenuListItem>
                <FooterMenuLink
                  to={`${config.AFFILIATE_WEB_ENDPOINT}affiliate/login`}
                  target="_blank"
                >
                  Affiliate Login
                </FooterMenuLink>
              </FooterMenuListItem>
              {/* <FooterMenuListItem>
              <FooterMenuLink
                to={`${config.WEB_ENDPOINT}affiliate/login`}
                target="_blank"
              >
                Become an Affiliate
              </FooterMenuLink>
            </FooterMenuListItem> */}
            </FooterMenuUnorderedList>
          </nav>
          <Link to="/platform/app/" target="_blank">
            <AppStoreImage
              src={AppStore}
              alt="Download the MindMe iOS application on the Apple App Store"
            />
          </Link>
        </FooterMenuContainer>
      </FooterLinksContainer>

      <FooterContactMenuContainer className="footer-bottom-row">
        <Link to="/">
          <MindMeLogo src={MindMeLogoWhite} alt="MindMe Logo" />
        </Link>
        <ContactLinksWrapper>
          {contactData.map(({ label, action }) => (
            <FooterMenuListItem className="footer-contact-list">
              <FooterOutboundLink href={action}>{label}</FooterOutboundLink>
            </FooterMenuListItem>
          ))}
        </ContactLinksWrapper>
        <SocialIconsWrapper>
          {socialFooterIcons.map(({ icon, alt, link }) => (
            <a href={link} target="_blank">
              <SocialFooterIcon src={icon} alt={alt} />
            </a>
          ))}
        </SocialIconsWrapper>
      </FooterContactMenuContainer>

      <FooterBottomBar>
        <FooterBottomBarContainer>
          <FooterMenuText>{copyWriteText}</FooterMenuText>
          <BottomBarLinksWrapper>
            {bottomBarMenu.map(({ name, url }) => (
              <FooterMenuListItem>
                <FooterMenuLink to={url}>{name}</FooterMenuLink>
              </FooterMenuListItem>
            ))}
          </BottomBarLinksWrapper>
        </FooterBottomBarContainer>
      </FooterBottomBar>
    </FooterWrapper>
  )
}

export default Footer

const AppStoreImage = styled.img`
  ${({ theme }) => theme.se`
  margin-bottom:0px;
  margin-top: 10px;
  `}
  ${({ theme }) => theme.tablet`
    margin-bottom: 25px;
    margin-top: 15px;
  `}
  ${({ theme }) => theme.desktop`
    width: 180px;
    margin-bottom: 25px;    
  `}
`

const MindMeLogo = styled.img`
  ${({ theme }) => theme.tablet`
    margin-bottom: 0;
  `}
`

const SocialFooterIcon = styled.img`
  margin-bottom: 0;
  ${({ theme }) => theme.se`    
    height: 35px;    
  `}
  ${({ theme }) => theme.tablet`    
    height: auto;
    margin-bottom: 0px; 
  `}
`

const SocialIconsWrapper = styled.div`
  a:not(:last-child) {
    margin-right: 10px;
  }
  ${({ theme }) => theme.se`
   margin-bottom: 20px;
  `}
  ${({ theme }) => theme.tablet`
    margin-bottom: 0px;
    margin-left: 50px;
  `}
  ${({ theme }) => theme.desktop`
    order: 3;
    width: 24%;
    margin-left: 25px;
    margin-bottom: 0px;
  `}
`

const FooterWrapper = styled.footer`
  display: flex;
  flex-direction: column;
  background: linear-gradient(269.93deg, #1e405a -3.63%, #152837 50.09%);
`

const FooterLinksContainer = styled(Container)`
  ${({ theme }) => theme.se`
    padding: 30px 20px 0px;
    align-items: flex-start;
  `}

  ${({ theme }) => theme.tablet`
    padding: 30px 20px 0px;
    justify-content: space-between;
  `}

  ${({ theme }) => theme.desktop`
    padding: 60px 20px 0;
    margin: auto;
  `}
`

const BottomBarLinksWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;

  ${({ theme }) => theme.se`
    order: 1;
  `}
  ${({ theme }) => theme.tablet`
    flex-direction: row;
    order: 2;
    flex: 2;
    justify-content:flex-start;
    align-items: center;

    > li:not(:last-child) {
      margin-right: 20px;
    }

    li {
      margin-bottom: 0;
      font-size: 20px;
    }
  `}
   ${({ theme }) => theme.desktop`
    justify-content:flex-end;
  `}
`

const ContactLinksWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;

  ${({ theme }) => theme.se`
    order: 2;
  `}
  ${({ theme }) => theme.tablet`
    flex-direction: column;
    flex: 2;
    justify-content: flex-end;    
    align-items: center;
    > li:not(:last-child) {
      margin-right: 20px;
    }
    > li{
      padding-bottom:10px;
      margin-bottom: 0px;
    }
  `}
  ${({ theme }) => theme.desktop`
     flex-direction: row;
   justify-content: flex-start;
   padding-left: 20px;
  `}
`

const FooterBottomBarContainer = styled(Container)`
  margin: auto;
  ${({ theme }) => theme.se`
    padding-left: 18px;   
  `}
  ${({ theme }) => theme.tablet`
    padding: 0 20px;
    margin: auto;
  `}
  ${({ theme }) => theme.desktop`
    padding: 0 20px;
    margin: auto;
  `}
  ${({ theme }) => theme.largeDesktop`
    padding: 0 20px;
    margin: auto;
  `}
`

const FooterBottomBar = styled.div`
  background: linear-gradient(to right, #000 60%, #393939);
  padding-top: 30px;
  padding-bottom: 30px;
`

const FooterMenuContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.se`
    margin-bottom: 20px;
    &.footer-menu-container{
      margin-bottom: 10px;
    }
  `}

  ${({ theme }) => theme.desktop`
    flex: 1;
    &.footer-menu-container{
      margin-bottom: 20px;
    }
  `}
`

const FooterContactMenuContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.se`
    padding: 0 20px;
  `}

  ${({ theme }) => theme.tablet`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    &.footer-bottom-row{
      align-items: center;
      margin-bottom: 20px;
    }
  `}

  ${({ theme }) => theme.desktop`
    padding-top: 20px;
    padding-bottom: 30px;
    margin:auto;
     &.footer-bottom-row{
      margin-bottom: 0px;
    }
  `}
`

const FooterMenuHeader = styled(H6)`
  color: #fff;
  margin-bottom: 20px;
  text-transform: uppercase;

  ${({ theme }) => theme.tablet`
    font-size: 26px;
  `}
`

const FooterMenuUnorderedList = styled.ul`
  margin: 0;
`

const FooterMenuListItem = styled.li`
  list-style: none;
  ${({ theme }) => theme.se`
   &.footer-contact-list {
     margin-bottom: 20px;
   }
  `}
  ${({ theme }) => theme.tablet`
   &.footer-contact-list {
     margin-bottom: 0px !important;
   }
  `}
  ${({ theme }) => theme.desktop`
   font-size: 16px;
  `}
`
const FooterMenuLink = styled(Link)`
  text-decoration: none;
  ${fontRegular()};
  color: #fff;

  ${({ theme }) => theme.tablet`
    font-size: 20px;
  `}
`
const FooterOutboundLink = styled.a`
  text-decoration: none;
  ${fontRegular()};
  color: #fff;

  ${({ theme }) => theme.tablet`

    font-size: 20px;
  `}
`

const FooterMenuText = styled(P)`
  color: #fff;
  margin-bottom: 0;

  ${({ theme }) => theme.se`
    order: 2;
  `}

  ${({ theme }) => theme.tablet`
    font-size: 20px;
    margin-bottom: 5px;
  `}

  ${({ theme }) => theme.desktop`
    order: 1;
    margin-bottom: 0px;
  `}
`
